import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientAuth } from '../contexts/ClientAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { LogIn, UserPlus, Eye, EyeOff, ArrowLeft } from 'lucide-react';

export default function ClientLoginPage() {
  const { login, register } = useClientAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await login(email, password); navigate('/my-bookings'); }
    catch (err) { setError(err.response?.data?.detail || 'Invalid credentials'); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await register(email, password, name, phone); navigate('/my-bookings'); }
    catch (err) { setError(err.response?.data?.detail || 'Registration failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F9F8F6' }}>
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/get-started')} className="flex items-center gap-1 text-sm text-[#5C6656] hover:text-[#556B2F]">
            <ArrowLeft size={16} /> Back
          </button>
          <LanguageSwitcher />
        </div>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#556B2F]" style={{ fontFamily: 'Outfit' }}>Apptio</h1>
          <p className="text-[#5C6656] text-sm mt-1">{t('publicBooking.bookWith')} Apptio</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#E9EDDF]">
            <TabsTrigger value="login" className="data-[state=active]:bg-[#556B2F] data-[state=active]:text-white" data-testid="client-login-tab">{t('login.loginTab')}</TabsTrigger>
            <TabsTrigger value="register" className="data-[state=active]:bg-[#556B2F] data-[state=active]:text-white" data-testid="client-register-tab">{t('login.registerTab')}</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <Card className="border-[#E5E7E1] shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('login.welcomeBack')}</CardTitle>
                <CardDescription className="text-[#5C6656]">View and manage your bookings</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('login.email')}</Label>
                    <Input data-testid="client-login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="border-[#E5E7E1]" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('login.password')}</Label>
                    <div className="relative">
                      <Input data-testid="client-login-password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required className="border-[#E5E7E1] pr-10" />
                      <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6656]">{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                    </div>
                  </div>
                  {error && <p className="text-sm text-[#E2725B]">{error}</p>}
                  <Button data-testid="client-login-btn" type="submit" disabled={loading} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white">
                    {loading ? '...' : <><LogIn size={16} className="mr-2" />{t('login.signIn')}</>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="register">
            <Card className="border-[#E5E7E1] shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('login.createAccount')}</CardTitle>
                <CardDescription className="text-[#5C6656]">Create an account to manage your bookings</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2"><Label>{t('login.name')} *</Label><Input data-testid="client-register-name" value={name} onChange={e => setName(e.target.value)} required className="border-[#E5E7E1]" /></div>
                  <div className="space-y-2"><Label>{t('login.email')} *</Label><Input data-testid="client-register-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="border-[#E5E7E1]" /></div>
                  <div className="space-y-2"><Label>{t('login.phone')}</Label><Input data-testid="client-register-phone" value={phone} onChange={e => setPhone(e.target.value)} className="border-[#E5E7E1]" /></div>
                  <div className="space-y-2"><Label>{t('login.password')} *</Label><Input data-testid="client-register-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="border-[#E5E7E1]" /></div>
                  {error && <p className="text-sm text-[#E2725B]">{error}</p>}
                  <Button data-testid="client-register-btn" type="submit" disabled={loading} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white">
                    {loading ? '...' : <><UserPlus size={16} className="mr-2" />{t('login.createAccount')}</>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
